import React from 'react';
import { siteResourceSourceRowsFromSed, siteResourceViewFromSed, type SiteResourceSedInput } from '../../../lib/sed/siteResourceDocumentProjection';

/** Show quoted service quantities without turning an allocation into a consumptive-use right. */
export function GenerationSiteWaterEvidence({ sed, onOpenDocument }: {
  sed?: SiteResourceSedInput | null;
  onOpenDocument?: (documentId?: string) => void;
}): React.JSX.Element | null {
  const view = siteResourceViewFromSed(sed);
  const rows = siteResourceSourceRowsFromSed(sed);
  const labels = {
    siteWaterAllocationMgd: 'Service allocation',
    projectAverageWaterDemandMgd: 'Average site demand',
    projectPeakWaterDemandMgd: 'Peak site demand',
    projectWaterMarginMgd: 'Stated service margin',
  } as const;
  const facts = view?.facts.filter(fact => fact.key in labels) ?? [];
  const conflicts = view?.conflicts.filter(key => key in labels) ?? [];
  if (!facts.length && !conflicts.length) return null;
  return (
    <section className="mt-3 rounded-lg border border-border bg-background/60 p-3" aria-label="Documented water service" data-testid="generation-water-service-evidence">
      <h3 className="text-xs font-bold text-text-primary">Water service from documents</h3>
      <dl className="mt-2 grid grid-cols-2 gap-3 text-xs">
        {facts.map(fact => (
          <div key={fact.key}>
            <dt className="text-text-muted">{labels[fact.key as keyof typeof labels]}</dt>
            <dd className="mt-0.5 font-bold tabular-nums text-text-primary">{typeof fact.value === 'number' ? (fact.value * 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 0 }) : fact.value} gal/day</dd>
            {(fact.assumed || fact.fictional) && <dd className="text-[10px] text-text-muted">Source planning assumption</dd>}
          </div>
        ))}
      </dl>
      <p className="mt-2 text-[10px] leading-relaxed text-text-muted">These are service and whole-site quantities. The consumptive-use limit below is a separate input; the calculated cooling subtotal is not whole-site demand.</p>
      {conflicts.length > 0 && <p className="mt-2 text-xs text-warning" role="alert">Conflicting document values require review before using the service figures.</p>}
      <details className="mt-2 text-[10px]">
        <summary className="cursor-pointer text-primary">View source statements</summary>
        {rows.filter(row => row.key in labels).map((row, index) => (
          <div key={`${row.fact.documentId}:${row.key}:${index}`} className="mt-2 border-t border-border pt-2">
            {onOpenDocument && <button type="button" onClick={() => onOpenDocument(row.fact.documentId)} className="text-primary underline-offset-2 hover:underline">{row.fact.documentName ?? row.fact.documentId}</button>}
            <p className="mt-1 text-text-muted">{row.fact.sourceQuote}</p>
          </div>
        ))}
      </details>
    </section>
  );
}
